import { Button } from "@/components/ui/button";
import { TenantLogo } from "@/components/TenantLogo";
import { Link } from "wouter";
import { Calendar, LogIn, LayoutDashboard, ClipboardList, User, LogOut, TrendingUp, KeyRound } from "lucide-react";
import { useAuth } from "@/_core/hooks/useAuth";
import { getLoginUrl } from "@/const";
import { hasAnyRole } from "@/lib/roles";
import { Skeleton } from "@/components/ui/skeleton";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { trpc } from "@/lib/trpc";

export function GymHeader() {
  const { user, loading: authLoading } = useAuth();

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container flex h-16 items-center justify-between">
        <Link href="/" className="flex items-center gap-3">
          <TenantLogo className="h-12 w-auto" />
        </Link>
        <nav className="flex items-center gap-4">
          <Link href="/gym/calendar">
            <Button variant="ghost" className="gap-2">
              <Calendar className="h-4 w-4" />
              <span className="hidden sm:inline">Kalender</span>
            </Button>
          </Link>
          <Link href="/gym/bookings">
            <Button variant="ghost" className="gap-2">
              <ClipboardList className="h-4 w-4" />
              <span className="hidden sm:inline">Meine Buchungen</span>
            </Button>
          </Link>
          <Link href="/gym/stats">
            <Button variant="ghost" className="gap-2">
              <TrendingUp className="h-4 w-4" />
              <span className="hidden sm:inline">Meine Statistiken</span>
            </Button>
          </Link>
          {authLoading ? (
            <Skeleton className="h-9 w-24" />
          ) : user ? (
            <>
              {hasAnyRole(user.roles, ["admin", "courseadmin"]) && (
                <Link href="/admin">
                  <Button variant="default" className="gap-2">
                    <LayoutDashboard className="h-4 w-4" />
                    <span className="hidden sm:inline">Admin</span>
                  </Button>
                </Link>
              )}
              <UserMenu user={user} />
            </>
          ) : (
            <Button variant="default" className="gap-2" onClick={() => window.location.href = getLoginUrl()}>
              <LogIn className="h-4 w-4" />
              <span className="hidden sm:inline">Anmelden</span>
            </Button>
          )}
        </nav>
      </div>
    </header>
  );
}


function UserMenu({ user }: { user: any }) {
  const { data: config } = trpc.config.getTenantConfig.useQuery();
  const logoutMutation = trpc.auth.logout.useMutation({
    onSuccess: () => {
      window.location.href = "/";
    }
  });
  
  const passwordChangeMutation = trpc.auth.getPasswordChangeUrl.useMutation({
    onSuccess: (data) => {
      window.location.href = data.url;
    },
    onError: (error) => {
      console.error('Password change error:', error);
      alert('Passwortänderung konnte nicht initialisiert werden. Bitte versuchen Sie es später erneut.');
    }
  });

  const handleChangePassword = () => {
    if (config?.authProvider !== 'auth0') {
      return;
    }
    passwordChangeMutation.mutate();
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="gap-2">
          <User className="h-4 w-4" />
          <span className="hidden sm:inline">
            {user.name?.split(" ")[0] || "Profil"}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium leading-none">{user.name}</p>
            {user.email && (
              <p className="text-xs leading-none text-muted-foreground">
                {user.email}
              </p>
            )}
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {config?.authProvider === 'auth0' && (
          <DropdownMenuItem
            onClick={handleChangePassword}
            disabled={passwordChangeMutation.isPending}
            className="cursor-pointer"
          >
            <KeyRound className="mr-2 h-4 w-4" />
            {passwordChangeMutation.isPending ? "Wird geladen..." : "Passwort ändern"}
          </DropdownMenuItem>
        )}
        <DropdownMenuItem
          onClick={() => logoutMutation.mutate()}
          disabled={logoutMutation.isPending}
          className="cursor-pointer"
        >
          <LogOut className="mr-2 h-4 w-4" />
          {logoutMutation.isPending ? "Abmelden..." : "Abmelden"}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
